/**
 * AIAction — the one ✨ control across Vault. Three states: idle, working, done.
 * It never writes silently: it runs an AI job, drops the result into a visible,
 * editable field via onResult, shows a one-line plain-English read-back, and
 * offers Undo so the write is reversible like any manual entry. On failure it
 * says so and leaves the field for the user to type — never a dead button.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useState } from 'react';
import { AskEnableAI, ensureAIConsent, useAI } from './AIGate.jsx';
import { useAuth } from '../App.jsx';

export default function AIAction({
  run, onResult, onUndo, readback, label = 'AI', title, disabled, size = 'md',
}) {
  const [state, setState] = useState('idle'); // idle | working | done
  const [line, setLine] = useState('');
  const [prev, setPrev] = useState(null);
  const ai = useAI();
  const { me } = useAuth() || {};

  const go = async () => {
    if (state === 'working') return;
    // Apple 5.1.1(i): ask before anything is sent to an AI provider. Nothing has
    // left the browser yet — the run() below is what would send it.
    if (!(await ensureAIConsent(me))) return;
    setState('working'); setLine('');
    try {
      const result = await run?.();
      if (result == null) { setState('idle'); return; }
      setPrev(result);
      onResult?.(result);
      setLine(typeof readback === 'function' ? readback(result)
                                             : (readback || 'Filled in — edit before saving.'));
      setState('done');
    } catch (e) {
      setLine(e?.data?.detail || 'AI could not read that — type it instead.');
      setState('idle');
    }
  };
  const undo = () => { onUndo?.(prev); setState('idle'); setLine(''); };

  const pad = size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm';
  // AI master switch off: not rendered, or shown disabled with the request link.
  if (ai.hidden) return null;
  if (ai.disabled) {
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <button type="button" disabled title="AI is switched off"
                className={`inline-flex items-center gap-1.5 rounded-xl border opacity-40 cursor-not-allowed ${pad}`}
                style={{ borderColor: 'var(--color-divider)', color: 'var(--color-text)' }}>
          <span aria-hidden>✨</span>{label}
        </button>
        <AskEnableAI feature={label || title || 'AI'} />
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button type="button" disabled={disabled || state === 'working'}
              onClick={go} title={title || 'Let AI fill this in'}
              className={`inline-flex items-center gap-1.5 rounded-xl border transition disabled:opacity-50 ${pad}`}
              style={{
                borderColor: 'var(--color-accent)', color: 'var(--color-accent)',
                background: state === 'done'
                  ? 'color-mix(in srgb, var(--color-accent) 12%, transparent)' : 'transparent',
              }}>
        <span aria-hidden>{state === 'working' ? '…' : '✨'}</span>
        {state === 'working' ? 'Reading…' : label}
      </button>
      {line && (
        <span className="text-xs flex items-center gap-2" style={{ color: 'var(--color-accent-400)' }}>
          <span className="min-w-0">{line}</span>
          {state === 'done' && onUndo && (
            <button type="button" onClick={undo}
                    className="underline shrink-0" style={{ color: 'var(--color-text)' }}>Undo</button>
          )}
        </span>
      )}
    </span>
  );
}
